import axios from 'axios';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

// Generate or retrieve device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('deviceId', deviceId);
  }
  return deviceId;
};

// Get screen resolution
const getScreenResolution = () => {
  return `${window.screen.width}x${window.screen.height}`;
};

// Get viewport size
const getViewportSize = () => {
  return `${window.innerWidth}x${window.innerHeight}`;
};

// Get browser language
const getLanguage = () => {
  return navigator.language || navigator.userLanguage || 'en';
};

// Get timezone
const getTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Get UTM parameters from URL
const getUTMParameters = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content')
  };
};

// Get page load time
const getPageLoadTime = () => {
  if (performance.timing) {
    return performance.timing.loadEventEnd - performance.timing.navigationStart;
  }
  return null;
};

// Get connection information
const getConnectionInfo = () => {
  if (navigator.connection) {
    return {
      effectiveType: navigator.connection.effectiveType,
      downlink: navigator.connection.downlink,
      rtt: navigator.connection.rtt
    };
  }
  return null;
};

// Track page view with duplicate prevention
const trackedPages = new Set();
const TRACKING_COOLDOWN = 1000; // 1 second cooldown

export const trackPageView = async (page, additionalData = {}) => {
  try {
    const now = Date.now();
    const trackingKey = `${page}_${now - (now % TRACKING_COOLDOWN)}`;
    
    // Prevent duplicate tracking within the cooldown period
    if (trackedPages.has(trackingKey)) {
      console.log('Analytics: Skipping duplicate page view for:', page);
      return;
    }
    
    trackedPages.add(trackingKey);
    
    // Clean up old tracking keys (keep only last 10)
    if (trackedPages.size > 10) {
      const keysArray = Array.from(trackedPages);
      trackedPages.clear();
      keysArray.slice(-5).forEach(key => trackedPages.add(key));
    }
    
    const analyticsData = {
      page,
      event_type: 'page_view',
      user_agent: navigator.userAgent,
      session_id: getSessionId(),
      device_id: getDeviceId(),
      screen_resolution: getScreenResolution(),
      viewport_size: getViewportSize(),
      language: getLanguage(),
      timezone: getTimezone(),
      ...getUTMParameters(),
      event_data: {
        page_load_time: getPageLoadTime(),
        connection_info: getConnectionInfo(),
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
        ...additionalData
      }
    };

    await axios.post('/api/analytics/track', analyticsData);
  } catch (error) {
    console.error('Analytics tracking failed:', error);
  }
};

// Track custom event
export const trackEvent = async (eventType, eventData = {}) => {
  try {
    const analyticsData = {
      page: window.location.pathname,
      event_type: eventType,
      user_agent: navigator.userAgent,
      session_id: getSessionId(),
      device_id: getDeviceId(),
      screen_resolution: getScreenResolution(),
      viewport_size: getViewportSize(),
      language: getLanguage(),
      timezone: getTimezone(),
      ...getUTMParameters(),
      event_data: {
        ...eventData,
        timestamp: new Date().toISOString()
      }
    };

    await axios.post('/api/analytics/track', analyticsData);
  } catch (error) {
    console.error('Event tracking failed:', error);
  }
};

// Track form submission
export const trackFormSubmission = async (formName, formData = {}) => {
  await trackEvent('form_submission', {
    form_name: formName,
    form_data: formData
  });
};

// Track button click
export const trackButtonClick = async (buttonName, buttonLocation = '') => {
  await trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation
  });
};

// Track link click
export const trackLinkClick = async (linkUrl, linkText = '') => {
  await trackEvent('link_click', {
    link_url: linkUrl,
    link_text: linkText
  });
};

// Track scroll depth
export const trackScrollDepth = async (scrollDepth) => {
  await trackEvent('scroll_depth', {
    scroll_depth: scrollDepth
  });
};

// Track time on page
export const trackTimeOnPage = async (timeOnPage) => {
  await trackEvent('time_on_page', {
    time_on_page: timeOnPage
  });
};

// Initialize analytics
export const initializeAnalytics = () => {
  // Track initial page view
  trackPageView(window.location.pathname);

  // Track scroll depth
  let maxScrollDepth = 0;
  const trackScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollDepth = Math.round((scrollTop / docHeight) * 100);
    
    if (scrollDepth > maxScrollDepth) {
      maxScrollDepth = scrollDepth;
      if (scrollDepth % 25 === 0) { // Track at 25%, 50%, 75%, 100%
        trackScrollDepth(scrollDepth);
      }
    }
  };

  window.addEventListener('scroll', trackScroll);

  // Track time on page
  const startTime = Date.now();
  window.addEventListener('beforeunload', () => {
    const timeOnPage = Date.now() - startTime;
    trackTimeOnPage(timeOnPage);
  });

  // Track clicks on external links
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (link && link.href && !link.href.startsWith(window.location.origin)) {
      trackLinkClick(link.href, link.textContent);
    }
  });
};
